import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import { getMe, getSubscriptionStatus, createSubscriptionCheckout } from "./lib/api";

import Dashboard from "./pages/Dashboard";
import HorseProfile from "./pages/HorseProfile";
import TracksPage from "./pages/TracksPage";
import TrackDetail from "./pages/TrackDetail";
import JockeyProfile from "./pages/JockeyProfile";
import ShowcardPage from "./pages/ShowcardPage";
import AuthGate from "./pages/AuthGate";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionManage from "./pages/SubscriptionManage";
import RawCompanyFooter from "./components/RawCompanyFooter";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Public routes that don't need auth
const PUBLIC_PATHS = ["/showcards/racing", "/terms", "/privacy", "/disclaimer", "/subscription/success", "/subscription/cancel"];

function ProtectedRoute({ children, user, subscriptionActive }) {
  const location = useLocation();
  if (PUBLIC_PATHS.some(p => location.pathname.startsWith(p))) return children;
  if (!user) return null; // AuthGate handles this
  if (!subscriptionActive && user.role !== "admin") {
    return <SubscriptionWall user={user} />;
  }
  return children;
}

function SubscriptionWall({ user }) {
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await createSubscriptionCheckout(window.location.origin);
      if (res.url) window.location.href = res.url;
      else if (res.already_subscribed) window.location.reload();
    } catch (err) {
      console.error("Subscription error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white p-4">
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Subscribe to Continue</h2>
        <p className="text-zinc-400 text-sm mb-6">Unlock full access to AI predictions, live streaming, daily tips, and more.</p>
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6 mb-6">
          <p className="text-[#D4AF37] font-mono text-xs uppercase tracking-widest mb-1">Monthly Subscription</p>
          <p className="text-white text-4xl font-black">£9.99<span className="text-sm text-zinc-400 font-normal">/month</span></p>
          <p className="text-zinc-500 text-xs mt-2">Cancel anytime</p>
        </div>
        <button
          data-testid="subscribe-btn"
          onClick={handleSubscribe}
          disabled={loading}
          className="w-full bg-[#E12B00] text-white font-mono text-sm font-bold uppercase tracking-wider py-4 hover:bg-[#C52400] transition-colors disabled:opacity-50"
        >
          {loading ? "Redirecting to Stripe..." : "Subscribe Now - £9.99/month"}
        </button>
        <p className="text-[10px] text-zinc-600 mt-3">Secured by Stripe. All odds are RAW Company Tech calculations, not official bookmaker odds.</p>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [allAccess, setAllAccess] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const me = await getMe();
        if (me && me.id) {
          setUser(me);
          const sub = await getSubscriptionStatus();
          setSubscriptionActive(sub.active || me.role === "admin");
          setAllAccess(sub.all_access || false);
        }
      } catch {
        // Not logged in
      }
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  const handleAuth = async (userData) => {
    setUser(userData);
    try {
      const sub = await getSubscriptionStatus();
      setSubscriptionActive(sub.active || userData.role === "admin");
      setAllAccess(sub.all_access || false);
    } catch {
      if (userData.role === "admin") setSubscriptionActive(true);
    }
  };

  if (!authChecked) {
    return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-zinc-500 font-mono text-sm">Loading...</div>
    </div>;
  }

  return (
    <AuthContext.Provider value={{ user, subscriptionActive, allAccess, setUser, setSubscriptionActive, setAllAccess }}>
      <BrowserRouter>
        <Routes>
          {/* Public routes - always accessible */}
          <Route path="/showcards/racing" element={<ShowcardPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<Navigate to="/" />} />

          {/* Protected routes - need auth + subscription */}
          {!user ? (
            <Route path="*" element={<AuthGate onAuth={handleAuth} />} />
          ) : (
            <>
              <Route path="/" element={
                <ProtectedRoute user={user} subscriptionActive={subscriptionActive}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/horse/:id" element={
                <ProtectedRoute user={user} subscriptionActive={subscriptionActive}>
                  <HorseProfile />
                </ProtectedRoute>
              } />
              <Route path="/tracks" element={
                <ProtectedRoute user={user} subscriptionActive={subscriptionActive}>
                  <TracksPage />
                </ProtectedRoute>
              } />
              <Route path="/track/:id" element={
                <ProtectedRoute user={user} subscriptionActive={subscriptionActive}>
                  <TrackDetail />
                </ProtectedRoute>
              } />
              <Route path="/jockey/:id" element={
                <ProtectedRoute user={user} subscriptionActive={subscriptionActive}>
                  <JockeyProfile />
                </ProtectedRoute>
              } />
              <Route path="/account" element={
                <ProtectedRoute user={user} subscriptionActive={subscriptionActive}>
                  <SubscriptionManage />
                </ProtectedRoute>
              } />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
        <RawCompanyFooter />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
